import React, { useMemo, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useGetPortalFeaturesQuery } from '../queries/getPortalFeatures.query.generated';
import FeatureTreeSelector from './FeatureTreeSelector';
import { buildFeatureTree, getChildIdsByRoot } from '../utils/featureTreeUtils';
import type { Feature } from '../types';

export interface FeaturePermissionsPanelProps {
  selectedIds?: Set<number> | number[];
  selectedNames?: string[];
  onChange?: (selectedIds: number[]) => void;
  onToggle?: (featureId: number, checked: boolean, cascadedIds: number[]) => void;
  readOnly?: boolean;
  showDescriptions?: boolean;
  lockedFeatureNames?: string[];
  targetUserTiers?: Set<string>;
  /** Service keys enabled on the user's rooftops; gates features with `config.requires`. */
  enabledServices?: Set<string>;
  /** When true, toggles are visible but non-interactive (e.g. caller lacks permission to edit). */
  disabled?: boolean;
}

const FeaturePermissionsPanel: React.FC<FeaturePermissionsPanelProps> = ({
  selectedIds,
  selectedNames,
  onChange,
  onToggle,
  readOnly = false,
  showDescriptions = true,
  lockedFeatureNames,
  targetUserTiers,
  enabledServices,
  disabled = false,
}) => {
  const { data, loading } = useGetPortalFeaturesQuery();

  const allFeatures = (data?.features ?? []) as Feature[];

  // Resolve locked feature names to IDs
  const lockedIds = useMemo(() => {
    if (!lockedFeatureNames || lockedFeatureNames.length === 0) return new Set<number>();
    const nameSet = new Set(lockedFeatureNames);
    return new Set(allFeatures.filter(f => nameSet.has(f.name)).map(f => f.id));
  }, [lockedFeatureNames, allFeatures]);

  // Resolve selected set
  const selectedSet = useMemo(() => {
    if (selectedIds) {
      return selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
    }
    if (selectedNames) {
      const nameSet = new Set(selectedNames);
      return new Set(allFeatures.filter(f => nameSet.has(f.name)).map(f => f.id));
    }
    return new Set<number>();
  }, [selectedIds, selectedNames, allFeatures]);

  const tree = useMemo(() => buildFeatureTree(allFeatures), [allFeatures]);
  const childrenByRoot = useMemo(() => getChildIdsByRoot(tree), [tree]);

  const handleToggle = useCallback(
    (featureId: number, checked: boolean) => {
      if (!checked && lockedIds.has(featureId)) return;

      const feature = allFeatures.find(f => f.id === featureId);
      if (!feature) return;

      const isRoot = feature.category_id == null;
      const cascadedIds: number[] = [];

      // Cascade: toggling a root OFF also removes its enabled children
      if (!checked && isRoot) {
        for (const childId of childrenByRoot.get(featureId) ?? []) {
          if (selectedSet.has(childId)) cascadedIds.push(childId);
        }
      }

      onToggle?.(featureId, checked, cascadedIds);

      if (onChange) {
        let next: number[];
        if (checked) {
          next = [...Array.from(selectedSet), featureId];
        } else {
          const toRemove = new Set([featureId, ...cascadedIds]);
          next = Array.from(selectedSet).filter(id => !toRemove.has(id));
        }
        onChange(next);
      }
    },
    [allFeatures, selectedSet, childrenByRoot, lockedIds, onChange, onToggle],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <FeatureTreeSelector
      features={allFeatures}
      selectedIds={selectedSet}
      disabled={disabled}
      disabledIds={lockedIds.size > 0 ? lockedIds : undefined}
      onToggle={readOnly ? undefined : handleToggle}
      mode="switch"
      readOnly={readOnly}
      showDescriptions={showDescriptions}
      targetUserTiers={targetUserTiers}
      enabledServices={enabledServices}
    />
  );
};

export default FeaturePermissionsPanel;
